import TMessage from "./TMessage";
import m from "mithril";
import TConversation from "./TConversation";
import {ChatbotRequestError} from "./ChatbotRequestError";

type TEndpoints = {
    conversation?: string,
    send?: string,
    stream?: string,
    download?: string,
}

export default class ChatbotApi {
    private cancelStream?: () => void;

    constructor(
        private readonly endpoints: TEndpoints
    ) { }

    public getConversation(timeout?: number): Promise<TConversation> {
        const url = this.endpoints.conversation;
        if (!url) {
            throw new Error('Chatbot api missing endpoint "conversation".');
        }
        return m.request({
            method: 'GET',
            url: url,
            timeout,
        });
    }

    public sendMessage(message: string, onMessage: (message: TMessage) => void): Promise<void> {
        const sendUrl = this.endpoints.send;
        const streamUrl = this.endpoints.stream;
        if (!sendUrl || !streamUrl) {
            throw new Error('Chatbot api missing endpoint "send" or "stream".');
        }
        if (!window.EventSource) {
            console.error('EventSource is not supported.');
        }

        return new Promise((resolve, reject) => {
            let source: EventSource | undefined;
            let cancelled = false;
            const fail = (error: unknown, phase: 'post' | 'stream' = 'stream') => {
                cancelled = true;
                source?.close();
                this.cancelStream = undefined;
                reject(new ChatbotRequestError(phase, error));
            };
            this.cancelStream = () => fail(new Error('Chatbot stream cancelled.'));
            m.request({
                method: 'POST',
                url: sendUrl,
                body: { message },
            }).then(() => {
                if (cancelled) {
                    return;
                }
                try {
                    source = new EventSource(streamUrl);
                    source.addEventListener('message', (e: MessageEvent) => {
                        try {
                            const data: TMessage = JSON.parse(e.data);
                            onMessage(data);
                            if (data.completed) {
                                source?.close();
                                this.cancelStream = undefined;
                                resolve();
                            }
                        } catch (error) {
                            fail(error);
                        }
                    });
                    source.addEventListener('error', error => fail(error));
                } catch (error) {
                    fail(error);
                }
            }, error => fail(error, 'post'));
        });
    }

    public closeStream(): void {
        this.cancelStream?.();
    }

    public getDownloadFileUrl(fileUuid: string, thumbnail: boolean): string {
        if (!this.endpoints.download) {
            throw new Error('Chatbot api missing endpoint "download" url.');
        }
        const delimiter = this.endpoints.download.includes('?') ? '&' : '?'
        return `${this.endpoints.download}${delimiter}fileUuid=${fileUuid}&thumbnail=${thumbnail?'1':'0'}`;
    }
}
